import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { CheckCircle, Save, Package, Calendar, Users, X, Check, Download, Plus, Trash2, Truck, Printer, Archive, Hash } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PinEntry } from './PinEntry';
import { getPinEntries, markPinEntriesExported } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OrderConfirm = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [editingItem, setEditingItem] = useState(null); // {orderId, itemIndex}
  const [editValues, setEditValues] = useState({ quantity: 0, unit_price: 0 });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [modifiedOrders, setModifiedOrders] = useState(new Set());
  const [addingToOrder, setAddingToOrder] = useState(null); // 追加中の注文ID
  const [viewMode, setViewMode] = useState('customer'); // 'customer' or 'supplier'
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'pin'
  const [customers, setCustomers] = useState([]); // 取引先データ（ダブル伝票用）
  const [pendingEdits, setPendingEdits] = useState({}); // 仕入れ先別一括編集用 { "orderId-itemIndex": {values} }
  const [newItem, setNewItem] = useState({
    item_name: '',
    origin: '',
    spec: '',
    unit_price: 0,
    quantity: 1,
    unit: '個'
  });

  // 仕入れ先別にグループ化したデータ
  const supplierGroups = useMemo(() => {
    const groups = {};
    for (const order of orders) {
      for (let itemIndex = 0; itemIndex < (order.items?.length || 0); itemIndex++) {
        const item = order.items[itemIndex];
        const supplierId = item.supplier_id || '_no_supplier';
        const supplierName = item.supplier_name || '仕入れ先未設定';
        if (!groups[supplierId]) {
          groups[supplierId] = { supplierName, items: [] };
        }
        groups[supplierId].items.push({
          ...item,
          orderId: order.id,
          itemIndex,
          customerName: order.customer_name || '-',
          customerCode: order.customer_code || '',
          routeNumber: order.route_number || '',
          orderStatus: order.status,
          isProxyOrder: order.is_proxy_order,
        });
      }
    }
    return Object.entries(groups).sort((a, b) => a[1].supplierName.localeCompare(b[1].supplierName));
  }, [orders]);

  useEffect(() => {
    generateDates();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchOrders();
    }
  }, [selectedDate]);

  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const d = addDays(today, i);
      dates.push(format(d, 'yyyy-MM-dd'));
    }
    setAvailableDates(dates);
    // デフォルトで明日を選択
    setSelectedDate(dates[1] || dates[0]);
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [ordersRes, customersRes] = await Promise.all([
        axios.get(`${API_URL}/api/orders`, { headers }),
        axios.get(`${API_URL}/api/customers`, { headers }),
      ]);
      
      const allCustomers = customersRes.data;
      setCustomers(allCustomers);
      
      // 顧客コード→顧客名のマッピング
      const codeToCustomer = {};
      allCustomers.forEach(c => { codeToCustomer[c.code] = c; });
      
      // 選択した日付の注文のみフィルタ
      const filtered = ordersRes.data.filter(order => order.delivery_date === selectedDate);
      
      // ダブル伝票の複製注文を生成
      const doubleOrders = [];
      for (const order of filtered) {
        const customer = allCustomers.find(c => c.id === order.customer_id || c.code === order.customer_code);
        if (customer?.double_invoice && customer?.double_invoice_customer_code) {
          const targetCustomer = codeToCustomer[customer.double_invoice_customer_code];
          if (targetCustomer) {
            doubleOrders.push({
              ...order,
              id: `double-${order.id}`,
              _source_order_id: order.id,
              customer_id: targetCustomer.id,
              customer_name: targetCustomer.name,
              customer_code: targetCustomer.code,
              route_number: targetCustomer.route_number || '',
              is_double_invoice: true,
              _original_customer_name: order.customer_name,
            });
          }
        }
      }
      
      setOrders([...filtered, ...doubleOrders]);
      setModifiedOrders(new Set());
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (orderId, itemIndex, item) => {
    setEditingItem({ orderId, itemIndex });
    setEditValues({
      item_name: item.item_name || '',
      origin: item.origin || '',
      spec: item.spec || '',
      unit_price: item.unit_price || 0,
      quantity: item.quantity || 0
    });
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditValues({ item_name: '', origin: '', spec: '', quantity: 0, unit_price: 0 });
  };

  const saveEdit = () => {
    if (!editingItem) return;
    
    const { orderId, itemIndex } = editingItem;
    setOrders(prev => {
      const updated = prev.map(order => {
        if (order.id === orderId) {
          const newItems = [...order.items];
          newItems[itemIndex] = {
            ...newItems[itemIndex],
            item_name: editValues.item_name,
            origin: editValues.origin,
            spec: editValues.spec,
            quantity: Number(editValues.quantity),
            unit_price: Number(editValues.unit_price),
            subtotal: Number(editValues.quantity) * Number(editValues.unit_price)
          };
          const newTotal = newItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
          return {
            ...order,
            items: newItems,
            total_amount: newTotal - (order.coupon_discount || 0)
          };
        }
        return order;
      });
      
      // ダブル伝票の同期: 元注文のアイテムをダブル伝票にコピー
      const editedOrder = updated.find(o => o.id === orderId);
      if (editedOrder && !editedOrder.is_double_invoice) {
        return updated.map(order => {
          if (order.is_double_invoice && order._source_order_id === orderId) {
            return { ...order, items: [...editedOrder.items], total_amount: editedOrder.total_amount };
          }
          return order;
        });
      }
      return updated;
    });
    
    setModifiedOrders(prev => new Set([...prev, orderId]));
    setEditingItem(null);
    toast.success('変更しました');
  };

  // === 仕入れ先別ビュー: 一括編集 ===
  const startSupplierEdit = (orderId, itemIndex, item) => {
    const key = `${orderId}-${itemIndex}`;
    setPendingEdits(prev => ({
      ...prev,
      [key]: {
        orderId,
        itemIndex,
        item_name: item.item_name || '',
        origin: item.origin || '',
        spec: item.spec || '',
        unit_price: item.unit_price || 0,
        quantity: item.quantity || 0,
      }
    }));
  };

  const updatePendingEdit = (key, field, value) => {
    setPendingEdits(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  const cancelPendingEdit = (key) => {
    setPendingEdits(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const cancelAllPendingEdits = (supplierId, groupItems) => {
    setPendingEdits(prev => {
      const next = { ...prev };
      for (const item of groupItems) {
        delete next[`${item.orderId}-${item.itemIndex}`];
      }
      return next;
    });
  };

  const savePendingEditsForSupplier = (supplierId, groupItems) => {
    const editsForGroup = groupItems
      .map(item => ({ item, edit: pendingEdits[`${item.orderId}-${item.itemIndex}`] }))
      .filter(({ edit }) => edit);

    if (editsForGroup.length === 0) return;

    setOrders(prev => {
      let updated = [...prev];
      const affectedOrderIds = new Set();

      for (const { edit } of editsForGroup) {
        const { orderId, itemIndex, item_name, origin, spec, quantity, unit_price } = edit;
        affectedOrderIds.add(orderId);
        updated = updated.map(order => {
          if (order.id === orderId) {
            const newItems = [...order.items];
            newItems[itemIndex] = {
              ...newItems[itemIndex],
              item_name,
              origin,
              spec,
              quantity: Number(quantity),
              unit_price: Number(unit_price),
              subtotal: Number(quantity) * Number(unit_price)
            };
            const newTotal = newItems.reduce((sum, i) => sum + (i.subtotal || 0), 0);
            return { ...order, items: newItems, total_amount: newTotal - (order.coupon_discount || 0) };
          }
          return order;
        });
      }

      // ダブル伝票の同期
      for (const oid of affectedOrderIds) {
        const editedOrder = updated.find(o => o.id === oid);
        if (editedOrder && !editedOrder.is_double_invoice) {
          updated = updated.map(order => {
            if (order.is_double_invoice && order._source_order_id === oid) {
              return { ...order, items: [...editedOrder.items], total_amount: editedOrder.total_amount };
            }
            return order;
          });
        }
      }

      return updated;
    });

    // 変更済みとしてマーク
    setModifiedOrders(prev => {
      const next = new Set(prev);
      for (const { edit } of editsForGroup) {
        next.add(edit.orderId);
      }
      return next;
    });

    // 保存済みの編集をクリア
    cancelAllPendingEdits(supplierId, groupItems);
    toast.success(`${editsForGroup.length}件の変更を登録しました`);
  };

  // === 仕入れ先別ビュー: キーボードナビゲーション ===
  const handleSupplierInputKeyDown = (e, supplierId, rowIdx, colName) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
      e.preventDefault();
      const direction = e.key === 'ArrowUp' ? -1 : 1;
      const targetRow = rowIdx + direction;
      const container = e.target.closest('table');
      if (!container) return;

      // 移動先の行がまだ編集中でなければ自動でeditモードに入れる
      const group = supplierGroups.find(([id]) => id === supplierId);
      if (group) {
        const targetItem = group[1].items[targetRow];
        if (targetItem && targetItem.orderStatus !== 'confirmed') {
          const targetKey = `${targetItem.orderId}-${targetItem.itemIndex}`;
          if (!pendingEdits[targetKey]) {
            startSupplierEdit(targetItem.orderId, targetItem.itemIndex, targetItem);
            // DOMが更新されるまで待ってからフォーカス
            setTimeout(() => {
              const target = container.querySelector(`[data-nav="${supplierId}-${targetRow}-${colName}"]`);
              if (target) { target.focus(); target.select?.(); }
            }, 30);
            return;
          }
        }
      }

      const target = container.querySelector(`[data-nav="${supplierId}-${targetRow}-${colName}"]`);
      if (target) {
        target.focus();
        target.select?.();
      }
    }
  };

  // ダブルクリックで編集開始
  const handleRowDoubleClick = (order, itemIndex, item) => {
    if (order.status === 'confirmed') {
      toast.error('確定済みの注文は編集できません');
      return;
    }
    if (order.is_double_invoice) {
      toast.error('ダブル伝票は元の注文を編集してください');
      return;
    }
    startEdit(order.id, itemIndex, item);
  };

  // 商品追加モード開始
  const startAddItem = (orderId) => {
    setAddingToOrder(orderId);
    setNewItem({
      item_name: '',
      origin: '',
      spec: '',
      unit_price: 0,
      quantity: 1,
      unit: '個'
    });
  };

  // 商品追加をキャンセル
  const cancelAddItem = () => {
    setAddingToOrder(null);
    setNewItem({
      item_name: '',
      origin: '',
      spec: '',
      unit_price: 0,
      quantity: 1,
      unit: '個'
    });
  };

  // 商品を追加
  const addItemToOrder = (orderId) => {
    if (!newItem.item_name.trim()) {
      toast.error('商品名を入力してください');
      return;
    }
    if (!newItem.unit_price || newItem.unit_price <= 0) {
      toast.error('単価を入力してください');
      return;
    }

    const subtotal = Number(newItem.quantity) * Number(newItem.unit_price);
    const itemToAdd = {
      item_id: `manual-${Date.now()}`,
      item_type: 'manual',
      item_name: newItem.item_name,
      origin: newItem.origin,
      spec: newItem.spec,
      unit_price: Number(newItem.unit_price),
      quantity: Number(newItem.quantity),
      unit: newItem.unit,
      subtotal: subtotal
    };

    setOrders(prev => {
      const updated = prev.map(order => {
        if (order.id === orderId) {
          const newItems = [...order.items, itemToAdd];
          const newTotal = newItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
          return { ...order, items: newItems, total_amount: newTotal - (order.coupon_discount || 0) };
        }
        return order;
      });
      // ダブル伝票を同期
      const editedOrder = updated.find(o => o.id === orderId);
      if (editedOrder && !editedOrder.is_double_invoice) {
        return updated.map(order => {
          if (order.is_double_invoice && order._source_order_id === orderId) {
            return { ...order, items: [...editedOrder.items], total_amount: editedOrder.total_amount };
          }
          return order;
        });
      }
      return updated;
    });

    setModifiedOrders(prev => new Set([...prev, orderId]));
    setAddingToOrder(null);
    toast.success('商品を追加しました');
  };

  // 商品を削除
  const deleteItem = (orderId, itemIndex) => {
    setOrders(prev => {
      const updated = prev.map(order => {
        if (order.id === orderId) {
          const newItems = order.items.filter((_, idx) => idx !== itemIndex);
          const newTotal = newItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
          return { ...order, items: newItems, total_amount: newTotal - (order.coupon_discount || 0) };
        }
        return order;
      });
      // ダブル伝票を同期
      const editedOrder = updated.find(o => o.id === orderId);
      if (editedOrder && !editedOrder.is_double_invoice) {
        return updated.map(order => {
          if (order.is_double_invoice && order._source_order_id === orderId) {
            return { ...order, items: [...editedOrder.items], total_amount: editedOrder.total_amount };
          }
          return order;
        });
      }
      return updated;
    });
    setModifiedOrders(prev => new Set([...prev, orderId]));
    toast.success('商品を削除しました');
  };

  const handleConfirmAll = () => {
    setConfirmDialogOpen(true);
  };

  // CSVエクスポート機能（PIN入力データも含む）
  const exportToCSV = async () => {
    // ヘッダー
    const headers = [
      '受注日時',
      '納品希望日',
      '取引先コード',
      '商品名',
      '数量',
      '特記事項',
      '商品コード',
      '注文に関する備考',
      '参考価格',
      '単位',
      '定貫区分',
      '金額（税込）',
      '消費税',
      '金額（税抜）',
      '顧客コード２',
      '取引先別'
    ];

    // データ行を生成
    const rows = [];
    for (const order of orders) {
      const orderDate = order.created_at 
        ? new Date(order.created_at).toLocaleString('ja-JP', { 
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
          }).replace(/\//g, '/')
        : '';
      
      for (const item of order.items || []) {
        rows.push([
          orderDate,                                         // 受注日時
          order.delivery_date || '',                         // 納品希望日
          order.customer_code || '',                         // 取引先コード
          item.item_name || '',                              // 商品名
          item.quantity ?? 0,                                // 数量
          '',                                                // 特記事項
          '11',                                              // 商品コード（デフォルト11）
          order.notes || '',                                 // 注文に関する備考
          item.unit_price != null ? item.unit_price : '',    // 参考価格
          item.unit || '',                                   // 単位
          '',                                                // 定貫区分
          item.subtotal != null ? item.subtotal : '',        // 金額（税込）
          '',                                                // 消費税
          '',                                                // 金額（税抜）
          order.customer_code || '',                         // 顧客コード２
          ''                                                 // 取引先別
        ]);
      }
    }

    // PIN入力データを追加
    let exportedPinIds = [];
    try {
      const pinRes = await getPinEntries(selectedDate ? selectedDate.substring(0, 7) : '');
      const pinEntries = pinRes.data || [];
      for (const entry of pinEntries) {
        const entryDate = entry.created_at
          ? new Date(entry.created_at).toLocaleString('ja-JP', {
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', second: '2-digit'
            }).replace(/\//g, '/')
          : '';
        rows.push([
          entryDate,                    // 受注日時
          entry.delivery_date || '',    // 納品希望日
          entry.customer_code || '',    // 取引先コード
          '売上入力',                    // 商品名
          1,                            // 数量
          '',                           // 特記事項
          '333',                        // 商品コード
          '',                           // 注文に関する備考
          '',                           // 参考価格
          '',                           // 単位
          '',                           // 定貫区分
          '',                           // 金額（税込）
          '',                           // 消費税
          entry.amount || 0,            // 金額（税抜）
          entry.customer_code || '',    // 顧客コード２
          ''                            // 取引先別
        ]);
        if (entry.id) exportedPinIds.push(entry.id);
      }
    } catch (error) {
      console.error('PIN entries fetch failed:', error);
    }

    // CSV文字列を生成（BOM付きUTF-8）
    const csvContent = '\uFEFF' + 
      headers.map(h => `"${h}"`).join(',') + '\n' +
      rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');

    // ダウンロード
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[-:T]/g, '').substring(0, 14);
    link.href = url;
    link.download = `注文リスト_${selectedDate}_${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // PIN伝票にCSV出力済フラグを記録
    if (exportedPinIds.length > 0) {
      try {
        await markPinEntriesExported(exportedPinIds);
      } catch (err) {
        console.error('mark-exported failed:', err);
      }
    }

    toast.success('CSVをエクスポートしました');
  };

  const confirmAllOrders = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // 未保存のpendingEditsを先にordersに反映する
      let currentOrders = [...orders];
      const pendingAffectedOrderIds = new Set();
      if (Object.keys(pendingEdits).length > 0) {
        for (const [key, edit] of Object.entries(pendingEdits)) {
          const { orderId, itemIndex, item_name, origin, spec, quantity, unit_price } = edit;
          pendingAffectedOrderIds.add(orderId);
          currentOrders = currentOrders.map(order => {
            if (order.id === orderId) {
              const newItems = [...order.items];
              newItems[itemIndex] = {
                ...newItems[itemIndex],
                item_name, origin, spec,
                quantity: Number(quantity),
                unit_price: Number(unit_price),
                subtotal: Number(quantity) * Number(unit_price)
              };
              const newTotal = newItems.reduce((sum, i) => sum + (i.subtotal || 0), 0);
              return { ...order, items: newItems, total_amount: newTotal - (order.coupon_discount || 0) };
            }
            return order;
          });
        }
        // ダブル伝票の同期
        for (const oid of pendingAffectedOrderIds) {
          const editedOrder = currentOrders.find(o => o.id === oid);
          if (editedOrder && !editedOrder.is_double_invoice) {
            currentOrders = currentOrders.map(order => {
              if (order.is_double_invoice && order._source_order_id === oid) {
                return { ...order, items: [...editedOrder.items], total_amount: editedOrder.total_amount };
              }
              return order;
            });
          }
        }
        setPendingEdits({});
      }

      // 変更のあった注文IDを統合
      const allModified = new Set([...modifiedOrders, ...pendingAffectedOrderIds]);
      
      // 実際の注文のみ処理（ダブル伝票の複製はスキップ）
      const realOrders = currentOrders.filter(o => !o.is_double_invoice);
      
      // 変更があった注文を保存
      for (const order of realOrders) {
        if (allModified.has(order.id)) {
          // アイテムの更新
          await axios.put(`${API_URL}/api/orders/${order.id}/items`, {
            items: order.items
          }, { headers });
        }
        // ステータスを「確定」に変更
        await axios.put(`${API_URL}/api/orders/${order.id}`, {
          status: 'confirmed'
        }, { headers });
      }
      
      toast.success(`${realOrders.length}件の注文を確定しました`);
      
      // CSVエクスポート用にcurrentOrdersを使用
      setOrders(currentOrders);
      // CSVエクスポート（ダブル伝票含む）
      exportToCSV();
      
      setConfirmDialogOpen(false);
      fetchOrders();
    } catch (error) {
      const message = error.response?.data?.detail || '確定に失敗しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // 集計
  const totalCustomers = new Set(orders.map(o => o.customer_name || o.customer_id)).size;
  const totalItems = orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);
  const totalAmount = orders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const confirmedCount = orders.filter(o => o.status === 'confirmed').length;

  if (loading && !selectedDate) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="order-confirm-page">
      <div className="page-header">
        <h1 className="page-title">注文確定</h1>
        <p className="text-sm text-slate-500">全店舗の注文内容を確認・修正して確定します</p>
      </div>

      {/* メインタブ切替（注文確認 / PIN入力） */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="order-confirm-tabs">
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'orders'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-orders"
        >
          <Package className="h-4 w-4 inline mr-1.5" />
          注文確認
        </button>
        <button
          onClick={() => setActiveTab('pin')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'pin'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-pin"
        >
          <Hash className="h-4 w-4 inline mr-1.5" />
          PIN入力
        </button>
      </div>

      {activeTab === 'pin' ? (
        <PinEntry selectedDate={selectedDate} />
      ) : (
      <>
      {/* 日付選択と集計 */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-slate-400" />
          <Select value={selectedDate} onValueChange={setSelectedDate}>
            <SelectTrigger className="w-48" data-testid="date-select">
              <SelectValue placeholder="配送日を選択" />
            </SelectTrigger>
            <SelectContent>
              {availableDates.map(date => (
                <SelectItem key={date} value={date}>
                  {format(new Date(date), 'MM月dd日(E)', { locale: ja })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="text-slate-600">取引先:</span>
            <span className="font-bold">{totalCustomers}件</span>
          </div>
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-slate-400" />
            <span className="text-slate-600">商品:</span>
            <span className="font-bold">{totalItems}点</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-600">合計:</span>
            <span className="font-bold font-mono text-lg">¥{totalAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* ビュー切替 */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="view-mode-toggle">
        <button
          onClick={() => { setViewMode('customer'); setEditingItem(null); setPendingEdits({}); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'customer' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="view-mode-customer"
        >
          <Users className="h-4 w-4 inline mr-1.5" />
          顧客別
        </button>
        <button
          onClick={() => { setViewMode('supplier'); setEditingItem(null); setPendingEdits({}); }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'supplier' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="view-mode-supplier"
        >
          <Truck className="h-4 w-4 inline mr-1.5" />
          仕入れ先別
        </button>
      </div>

      {/* ステータスバッジと確定ボタン */}
      <div className="flex flex-wrap items-center gap-4">
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
          受付中: {pendingCount}件
        </Badge>
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          確定済: {confirmedCount}件
        </Badge>
        {modifiedOrders.size > 0 && (
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
            変更あり: {modifiedOrders.size}件
          </Badge>
        )}
        <div className="flex-1"></div>
        {orders.length > 0 && (
          <Button 
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-confirm-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            印刷
          </Button>
        )}
        {orders.length > 0 && (
          <Button 
            variant="outline"
            onClick={exportToCSV}
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            CSVエクスポート
          </Button>
        )}
        {orders.length > 0 && pendingCount > 0 && (
          <Button 
            onClick={handleConfirmAll}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="confirm-all-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            全て確定する ({pendingCount}件)
          </Button>
        )}
      </div>

      {/* 注文一覧 */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
        </div>
      ) : orders.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>この日の注文はありません</p>
          </CardContent>
        </Card>
      ) : viewMode === 'customer' ? (
        /* ========== 顧客別ビュー ========== */
        <div className="space-y-4">
          {orders.map(order => (
            <Card 
              key={order.id} 
              className={`border-slate-200 ${modifiedOrders.has(order.id) ? 'ring-2 ring-purple-300' : ''} ${order.is_double_invoice ? 'border-amber-300 bg-amber-50/30' : ''}`}
            >
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">
                      {order.customer_name || '-'}
                    </CardTitle>
                    {order.customer_code && (
                      <Badge variant="outline" className="font-mono text-xs">
                        {order.customer_code}
                      </Badge>
                    )}
                    {order.route_number && (
                      <Badge variant="outline" className="text-xs">
                        ルート: {order.route_number}
                      </Badge>
                    )}
                    {order.is_proxy_order && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs">代理発注</Badge>
                    )}
                    {order.is_double_invoice && (
                      <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid="double-invoice-badge">
                        W伝票（元: {order._original_customer_name}）
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={order.status === 'confirmed' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                      {order.status === 'confirmed' ? '確定' : '受付中'}
                    </Badge>
                    <span className="font-mono font-bold text-lg">
                      ¥{order.total_amount?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-400 mb-2">※ 行をダブルクリックで編集できます</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-2 font-medium">商品名</th>
                      <th className="py-2 font-medium">産地/規格</th>
                      <th className="py-2 font-medium text-right">単価</th>
                      <th className="py-2 font-medium text-right">数量</th>
                      <th className="py-2 font-medium text-right">小計</th>
                      {order.status !== 'confirmed' && !order.is_double_invoice && <th className="py-2 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((item, idx) => {
                      const isEditing = editingItem?.orderId === order.id && editingItem?.itemIndex === idx;
                      return (
                        <tr 
                          key={idx} 
                          className={`border-b border-slate-100 ${!isEditing && order.status !== 'confirmed' ? 'cursor-pointer hover:bg-slate-50' : ''} ${isEditing ? 'bg-blue-50' : ''}`}
                          onDoubleClick={() => handleRowDoubleClick(order, idx, item)}
                        >
                          <td className="py-2 font-medium">
                            {isEditing ? (
                              <Input
                                value={editValues.item_name}
                                onChange={(e) => setEditValues(prev => ({ ...prev, item_name: e.target.value }))}
                                className="h-7 text-sm"
                                autoFocus
                              />
                            ) : (
                              item.item_name
                            )}
                          </td>
                          <td className="py-2 text-slate-500">
                            {isEditing ? (
                              <div className="flex gap-1">
                                <Input
                                  value={editValues.origin}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, origin: e.target.value }))}
                                  className="h-7 text-sm w-20"
                                  placeholder="産地"
                                />
                                <span className="self-center">/</span>
                                <Input
                                  value={editValues.spec}
                                  onChange={(e) => setEditValues(prev => ({ ...prev, spec: e.target.value }))}
                                  className="h-7 text-sm w-20"
                                  placeholder="規格"
                                />
                              </div>
                            ) : (
                              <>{item.origin || '-'} / {item.spec || '-'}</>
                            )}
                          </td>
                          <td className="py-2 text-right font-mono">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editValues.unit_price}
                                onChange={(e) => setEditValues(prev => ({ ...prev, unit_price: e.target.value }))}
                                className="w-24 h-7 text-right text-sm"
                              />
                            ) : (
                              `¥${item.unit_price?.toLocaleString()}`
                            )}
                          </td>
                          <td className="py-2 text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editValues.quantity}
                                onChange={(e) => setEditValues(prev => ({ ...prev, quantity: e.target.value }))}
                                className="w-20 h-7 text-right text-sm"
                              />
                            ) : (
                              <span className="font-mono">{item.quantity}{item.unit}</span>
                            )}
                          </td>
                          <td className="py-2 text-right font-mono font-medium">
                            {isEditing 
                              ? `¥${(Number(editValues.quantity) * Number(editValues.unit_price)).toLocaleString()}`
                              : `¥${item.subtotal?.toLocaleString()}`
                            }
                          </td>
                          {order.status !== 'confirmed' && !order.is_double_invoice && (
                            <td className="py-2 text-center">
                              {!isEditing && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteItem(order.id, idx);
                                  }}
                                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                  title="削除"
                                  data-testid={`delete-item-${order.id}-${idx}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {/* 商品追加フォーム行 */}
                    {addingToOrder === order.id && (
                      <tr className="bg-green-50 border-b border-green-200">
                        <td className="py-2">
                          <Input
                            value={newItem.item_name}
                            onChange={(e) => setNewItem(prev => ({ ...prev, item_name: e.target.value }))}
                            placeholder="商品名 *"
                            className="h-8 text-sm"
                            autoFocus
                            data-testid="new-item-name"
                          />
                        </td>
                        <td className="py-2">
                          <div className="flex gap-1">
                            <Input
                              value={newItem.origin}
                              onChange={(e) => setNewItem(prev => ({ ...prev, origin: e.target.value }))}
                              placeholder="産地"
                              className="h-8 text-sm w-20"
                            />
                            <span className="self-center">/</span>
                            <Input
                              value={newItem.spec}
                              onChange={(e) => setNewItem(prev => ({ ...prev, spec: e.target.value }))}
                              placeholder="規格"
                              className="h-8 text-sm w-20"
                            />
                          </div>
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            value={newItem.unit_price}
                            onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: e.target.value }))}
                            placeholder="単価 *"
                            className="h-8 text-sm w-24 text-right"
                            data-testid="new-item-price"
                          />
                        </td>
                        <td className="py-2">
                          <Input
                            type="number"
                            value={newItem.quantity}
                            onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
                            placeholder="数量"
                            className="h-8 text-sm w-20 text-right"
                            data-testid="new-item-quantity"
                          />
                        </td>
                        <td className="py-2 text-right font-mono text-sm text-slate-500">
                          ¥{(Number(newItem.quantity || 0) * Number(newItem.unit_price || 0)).toLocaleString()}
                        </td>
                        <td className="py-2">
                          <div className="flex gap-1">
                            <button
                              onClick={() => addItemToOrder(order.id)}
                              className="p-1 text-green-600 hover:text-green-700"
                              title="追加"
                              data-testid="confirm-add-item"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={cancelAddItem}
                              className="p-1 text-slate-400 hover:text-slate-600"
                              title="キャンセル"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* 商品追加ボタン */}
                {order.status !== 'confirmed' && !order.is_double_invoice && addingToOrder !== order.id && (
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startAddItem(order.id)}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      data-testid={`add-item-btn-${order.id}`}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      商品を追加
                    </Button>
                  </div>
                )}
                {/* 編集中の保存・キャンセルボタン */}
                {editingItem?.orderId === order.id && (
                  <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEdit}
                    >
                      <X className="h-4 w-4 mr-1" />
                      キャンセル
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={saveEdit}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      保存
                    </Button>
                  </div>
                )}
                {order.notes && (
                  <div className="mt-3 p-2 bg-slate-50 rounded text-sm text-slate-600">
                    備考: {order.notes}
                  </div>
                )}
                {order.coupon_discount > 0 && (
                  <div className="mt-2 text-right text-sm text-purple-600">
                    クーポン割引: -¥{order.coupon_discount?.toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* ========== 仕入れ先別ビュー ========== */
        <div className="space-y-4">
          {supplierGroups.map(([supplierId, group]) => {
            const pendingKeys = group.items.map(item => `${item.orderId}-${item.itemIndex}`).filter(k => pendingEdits[k]);
            const hasPendingEdits = pendingKeys.length > 0;
            const groupSubtotal = group.items.reduce((sum, item) => {
              const key = `${item.orderId}-${item.itemIndex}`;
              const edit = pendingEdits[key];
              if (edit) return sum + Number(edit.quantity) * Number(edit.unit_price);
              return sum + (item.subtotal || 0);
            }, 0);
            return (
              <Card key={supplierId} className="border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <Truck className="h-5 w-5 text-slate-400" />
                      <CardTitle className="text-base">{group.supplierName}</CardTitle>
                      <Badge variant="outline" className="text-xs">{group.items.length}品</Badge>
                      {hasPendingEdits && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">{pendingKeys.length}件 編集中</Badge>
                      )}
                    </div>
                    <span className="font-mono font-bold text-lg">¥{groupSubtotal.toLocaleString()}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400 mb-2">※ 行をダブルクリックで編集、複数行を編集後に一括登録できます</p>
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="border-b text-left text-slate-500">
                        <th className="py-2 font-medium w-[20%]">商品名</th>
                        <th className="py-2 font-medium w-[15%]">産地/規格</th>
                        <th className="py-2 font-medium text-right w-[10%]">単価</th>
                        <th className="py-2 font-medium text-right w-[8%]">数量</th>
                        <th className="py-2 font-medium text-right w-[10%]">小計</th>
                        <th className="py-2 font-medium w-[16%]">注文先</th>
                        <th className="py-2 font-medium text-center w-[7%]">ST</th>
                        <th className="py-2 w-[6%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item, idx) => {
                        const editKey = `${item.orderId}-${item.itemIndex}`;
                        const edit = pendingEdits[editKey];
                        const isEditing = !!edit;
                        const isConfirmed = item.orderStatus === 'confirmed';
                        const displayQty = isEditing ? Number(edit.quantity) : item.quantity;
                        const displayPrice = isEditing ? Number(edit.unit_price) : item.unit_price;
                        const displaySubtotal = isEditing ? displayQty * displayPrice : item.subtotal;
                        return (
                          <tr
                            key={editKey}
                            className={`border-b border-slate-100 h-10 ${item.is_stock ? 'bg-orange-50' : ''} ${!isEditing && !isConfirmed ? 'cursor-pointer hover:bg-slate-50' : ''} ${isEditing ? 'bg-blue-50' : ''}`}
                            onDoubleClick={() => {
                              if (isConfirmed) {
                                toast.error('確定済みの注文は編集できません');
                                return;
                              }
                              if (!isEditing) {
                                startSupplierEdit(item.orderId, item.itemIndex, item);
                              }
                            }}
                          >
                            <td className="py-1 font-medium truncate">
                              {isEditing ? (
                                <Input
                                  value={edit.item_name}
                                  onChange={(e) => updatePendingEdit(editKey, 'item_name', e.target.value)}
                                  className="h-8 text-sm"
                                  data-nav={`${supplierId}-${idx}-name`}
                                  onKeyDown={(e) => handleSupplierInputKeyDown(e, supplierId, idx, 'name')}
                                />
                              ) : (
                                <span className="block truncate">{item.item_name}</span>
                              )}
                            </td>
                            <td className="py-1 text-slate-500">
                              {isEditing ? (
                                <div className="flex gap-1 items-center">
                                  <Input
                                    value={edit.origin}
                                    onChange={(e) => updatePendingEdit(editKey, 'origin', e.target.value)}
                                    className="h-8 text-sm flex-1 min-w-0"
                                    placeholder="産地"
                                    data-nav={`${supplierId}-${idx}-origin`}
                                    onKeyDown={(e) => handleSupplierInputKeyDown(e, supplierId, idx, 'origin')}
                                  />
                                  <span>/</span>
                                  <Input
                                    value={edit.spec}
                                    onChange={(e) => updatePendingEdit(editKey, 'spec', e.target.value)}
                                    className="h-8 text-sm flex-1 min-w-0"
                                    placeholder="規格"
                                    data-nav={`${supplierId}-${idx}-spec`}
                                    onKeyDown={(e) => handleSupplierInputKeyDown(e, supplierId, idx, 'spec')}
                                  />
                                </div>
                              ) : (
                                <span className="block truncate">{item.origin || '-'} / {item.spec || '-'}</span>
                              )}
                            </td>
                            <td className="py-1 text-right font-mono">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={edit.unit_price}
                                  onChange={(e) => updatePendingEdit(editKey, 'unit_price', e.target.value)}
                                  className="h-8 text-right text-sm w-full"
                                  data-nav={`${supplierId}-${idx}-price`}
                                  onKeyDown={(e) => handleSupplierInputKeyDown(e, supplierId, idx, 'price')}
                                />
                              ) : (
                                `¥${item.unit_price?.toLocaleString()}`
                              )}
                            </td>
                            <td className="py-1 text-right">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={edit.quantity}
                                  onChange={(e) => updatePendingEdit(editKey, 'quantity', e.target.value)}
                                  className="h-8 text-right text-sm w-full"
                                  data-nav={`${supplierId}-${idx}-qty`}
                                  onKeyDown={(e) => handleSupplierInputKeyDown(e, supplierId, idx, 'qty')}
                                />
                              ) : (
                                <span className="font-mono">{item.quantity}</span>
                              )}
                            </td>
                            <td className="py-1 text-right font-mono font-medium">
                              ¥{displaySubtotal?.toLocaleString()}
                            </td>
                            <td className="py-1">
                              <div className="flex items-center gap-1 truncate">
                                <span className="text-slate-700 text-xs truncate">{item.customerName}</span>
                                {item.customerCode && (
                                  <span className="text-slate-400 text-xs font-mono flex-shrink-0">({item.customerCode})</span>
                                )}
                              </div>
                            </td>
                            <td className="py-1 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // ストックフラグをトグル
                                  setOrders(prev => prev.map(order => {
                                    if (order.id === item.orderId) {
                                      const newItems = [...order.items];
                                      newItems[item.itemIndex] = { ...newItems[item.itemIndex], is_stock: !newItems[item.itemIndex].is_stock };
                                      return { ...order, items: newItems };
                                    }
                                    return order;
                                  }));
                                  setModifiedOrders(prev => new Set([...prev, item.orderId]));
                                }}
                                className={`p-1 rounded transition-colors ${item.is_stock ? 'bg-orange-100 text-orange-600' : 'text-slate-300 hover:text-orange-400'}`}
                                title={item.is_stock ? 'ストック解除' : 'ストックマーク'}
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                            </td>
                            <td className="py-1 text-center">
                              {isEditing ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelPendingEdit(editKey);
                                  }}
                                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                                  title="編集取消"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              ) : !isConfirmed ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteItem(item.orderId, item.itemIndex);
                                  }}
                                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                  title="削除"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* 一括登録ボタン */}
                  {hasPendingEdits && (
                    <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                      <Button variant="outline" size="sm" onClick={() => cancelAllPendingEdits(supplierId, group.items)}>
                        <X className="h-4 w-4 mr-1" />全てキャンセル
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => savePendingEditsForSupplier(supplierId, group.items)}>
                        <Check className="h-4 w-4 mr-1" />{pendingKeys.length}件を一括登録
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* 確定確認ダイアログ */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>注文を確定しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedDate && format(new Date(selectedDate), 'MM月dd日(E)', { locale: ja })}配送分の
              受付中の注文 {pendingCount}件 を確定します。
              {modifiedOrders.size > 0 && (
                <span className="block mt-2 text-purple-600">
                  ※ {modifiedOrders.size}件の注文に変更があります
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmAllOrders}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={saving}
            >
              {saving ? '処理中...' : '確定する'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 印刷用CSS */}
      <style>{`
        @media print {
          aside, header, nav, [class*="sidebar"], [class*="Sidebar"], .no-print {
            display: none !important;
            width: 0 !important; height: 0 !important;
          }
          body * { visibility: hidden; }
          [data-testid="order-confirm-page"], [data-testid="order-confirm-page"] * { visibility: visible; }
          [data-testid="order-confirm-page"] { position: absolute; left: 0; top: 0; width: 100%; }
          [data-testid="view-mode-toggle"], [data-testid="confirm-all-btn"], [data-testid="export-csv-btn"], [data-testid="print-confirm-btn"], button { display: none !important; }
          [data-testid="order-confirm-page"] table { visibility: visible; }
          body { font-size: 8pt; }
          table { font-size: 7pt; width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 2px 4px; }
          .bg-orange-50 { background: #fff7ed !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .bg-orange-100 { background: #ffedd5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @page { size: A4 landscape; margin: 8mm; }
      `}</style>
    </div>
  );
};
